import { useState, useEffect, useCallback } from 'react';
import type { Announcement, CreateAnnouncementRequest, UpdateAnnouncementRequest } from './models';
import {
  listAnnouncements, createAnnouncement, updateAnnouncement,
  deleteAnnouncement, getUnreadAnnouncementCount, markAnnouncementRead,
} from './services';

/** Admin page — full CRUD list */
export function useAnnouncementList() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [size] = useState(20);

  const load = useCallback(async (p?: number) => {
    setLoading(true);
    try {
      const res = await listAnnouncements(p ?? page, size);
      setItems(res.items ?? []);
      setTotal(res.total);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = useCallback(async (data: CreateAnnouncementRequest) => {
    await createAnnouncement(data);
    load(1);
    setPage(1);
  }, [load]);

  const handleUpdate = useCallback(async (id: string, data: UpdateAnnouncementRequest) => {
    await updateAnnouncement(id, data);
    load();
  }, [load]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteAnnouncement(id);
    load();
  }, [load]);

  return { items, total, loading, page, size, setPage, load, handleCreate, handleUpdate, handleDelete };
}

/** Bell icon badge — polls unread count every 60s */
export function useUnreadAnnouncementCount() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let active = true;
    const fetch = () => {
      getUnreadAnnouncementCount()
        .then(c => { if (active) setCount(c); })
        .catch(() => {});
    };
    fetch();
    const timer = setInterval(fetch, 60_000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  return count;
}

/** Drawer — published announcements for all users */
export function useAnnouncementDrawer() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAnnouncements(1, 50, 'published');
      setItems(res.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: string) => {
    await markAnnouncementRead(id);
    setItems(prev => prev.map(item => item.id === id ? { ...item, isRead: true } : item));
  }, []);

  return { items, loading, load, markRead };
}
